import workspace from "_src/workspace";
import {mat4} from "_src/libs/gl-matrix";
import {objectType} from "_src/constants";
import {messageType, resultStatus, workerTypes} from "_src/engine/ipc";
import workerManager from "_src/engine/worker_manager";
import memoryProfiler from "_src/engine/memory_profiler";
import output from "_src/engine/output";
import workerPool from "_src/engine/worker_pool";

export default {
  init() {
    const message = {
      type : messageType.INIT_WASM,
      tid : workerManager.generateUniqueID(),
    };

    return workerManager.sendMessage(message, [], message.tid).catch((error) => {
      if (error.code !== resultStatus.TERMINATED) {
        output.error(output.DEVELOPERS_ERROR, "Can't init wasm module. " + error.message);
      }
    });
  },

  horn(fromPoints, toPoints) {
    const message = {
      fromPoints : fromPoints,
      toPoints : toPoints,
      type : messageType.HORN_ALIGN,
      tid : workerManager.generateUniqueID(),
    };

    return workerManager.sendMessage(message, [], message.tid).then(({ data }) => {
      if (data.status === resultStatus.ALL_OK) {
        return data.matrix;
      }
    }).catch((error) => {
      if (error.code !== resultStatus.TERMINATED) {
        output.error(output.DEVELOPERS_ERROR, "Can't perform horn align. " + error.message);
      }
    });
  },

  ICP(fromObjects, toObjects) {
    const scannerType = getScannerType(fromObjects.concat(toObjects));
    const message = {
      fromData : [],
      toData : [],
      type : messageType.ICP_ALIGN,
      tid : workerManager.generateUniqueID(),
      scannerType: scannerType,
    };

    const transferables = [];

    objectsToICPData(fromObjects, message.fromData, transferables);
    objectsToICPData(toObjects, message.toData, transferables);

    for (const buffer of transferables) {
      memoryProfiler.markMemoryReleased(buffer);
    }

    return workerManager.sendMessage(message, transferables, message.tid).then(({ data }) => {
      if (data.status === resultStatus.ALL_OK) {
        ICPDataToObjects(fromObjects, data.fromData);
        ICPDataToObjects(toObjects, data.toData);
        return data.matrix;
      }
    }).catch((error) => {
      if (error.code !== resultStatus.TERMINATED) {
        output.error(output.DEVELOPERS_ERROR, "Can't perform ICP align. " + error.message);
      }
    });
  },

  bakeDM(srcObject, tgtObject, distance) {
    const message = {
      srcData: null,
      tgtData: null,
      distance: distance,
      type: messageType.BAKE_DM,
      tid: workerManager.generateUniqueID(),
    };

    const transferables = [];

    message.srcData = objectToDMBakerData(srcObject, transferables, true);
    message.tgtData = objectToDMBakerData(tgtObject, transferables, false);

    for (const buffer of transferables) {
      memoryProfiler.markMemoryReleased(buffer);
    }

    return workerManager.sendMessage(message, transferables, message.tid).then(({data}) => {
      if (data.status === resultStatus.ALL_OK) {
        DMBakerDataToObject(srcObject, data.srcData, true);
        DMBakerDataToObject(tgtObject, data.tgtData, false);

        return {
          dm: data.dm,
          dmInfo: data.dmInfo,
        };
      }
    }).catch((error) => {
      if (error.code !== resultStatus.TERMINATED) {
        output.error(output.DEVELOPERS_ERROR, "Can't perform distance map baking. " + error.message);
      }
    });
  },

  cleanup() {
    workerPool.cleanup(workerTypes.WASM_PROCESSOR);
  }
}

function getScannerType(objects) {
  for (const objectUUID of objects) {
    const object = workspace.getObjectByUUID(objectUUID);

    if (object.getScannerType()) {
      return object.getScannerType();
    }
    if (object.getDeviceSerial()) {
      return object.getDeviceSerial();
    }
  }

  return "";
}

function objectToDMBakerData(object, transferables, isSrcObject) {
    const DMBakerData = {
      pointsChunks: [],
      normalsChunks: [],
      trianglesChunks: [],
      transform: mat4.transpose(mat4.create(), object.model.modelMatrix),
    };

    for (const chunk of object.frames[0].model.chunks) {
      const positionsItem = chunk.geometry.getLayoutItem("a_position");
      const positions = positionsItem.getBuffer();
      DMBakerData.pointsChunks.push(positions);
      transferables.push(positions.buffer);
      positionsItem.setIsDetached(true);

      if (isSrcObject) {
        const normalsItem = chunk.geometry.getLayoutItem("a_normal");
        const normals = normalsItem.getBuffer();
        DMBakerData.normalsChunks.push(normals);
        transferables.push(normals.buffer);
        normalsItem.setIsDetached(true);
      } else {
        const trianglesItem = chunk.geometry.getLayoutItem("indices");
        const triangles = trianglesItem.getBuffer();
        DMBakerData.trianglesChunks.push(triangles);
        transferables.push(triangles.buffer);
        trianglesItem.setIsDetached(true);
      }
    }

    return DMBakerData;
}

function DMBakerDataToObject(object, DMBakerData, isSrcObject) {
  for (let i=0; i<object.frames[0].model.chunks.length; i++) {
    const chunk = object.frames[0].model.chunks[i];

    const positionsItem = chunk.geometry.getLayoutItem("a_position");
    positionsItem.setBuffer(DMBakerData.pointsChunks[i].buffer);

    if (isSrcObject) {
      const normalsItem = chunk.geometry.getLayoutItem("a_normal");
      normalsItem.setBuffer(DMBakerData.normalsChunks[i].buffer);
    } else {
      const trianglesItem = chunk.geometry.getLayoutItem("indices");
      trianglesItem.setBuffer(DMBakerData.trianglesChunks[i].buffer);
    }
  }
}

function objectsToICPData(objects, ICPData, transferables) {
  for (const objectUUID of objects) {
    const object = workspace.getObjectByUUID(objectUUID);
    const filterFrames = object.type === objectType.SCAN;

    for (const frame of object.frames) {
      if (filterFrames && !frame.isKeyFrame() && !frame.textureList.length) {
        continue;
      }

      const frameData = {
        pointsChunks: [],
        normalsChunks: [],
        sizeChunks: [],
        trianglesChunks: [],
        transform: mat4.transpose(mat4.create(), frame.model.modelMatrix),
      };

      for (const chunk of frame.model.chunks) {
        const positionsItem = chunk.geometry.getLayoutItem("a_position");
        const positions = positionsItem.getBuffer();
        frameData.pointsChunks.push(positions);
        transferables.push(positions.buffer);
        positionsItem.setIsDetached(true);

        if (object.type === objectType.SURFELS) {
          const normalsItem = chunk.geometry.getLayoutItem("a_normal");
          const sizeItem = chunk.geometry.getLayoutItem("a_size");

          const normals = normalsItem.getBuffer();
          const size = sizeItem.getBuffer();

          frameData.normalsChunks.push(normals);
          frameData.sizeChunks.push(size);

          transferables.push(normals.buffer);
          transferables.push(size.buffer);

          normalsItem.setIsDetached(true);
          sizeItem.setIsDetached(true);
        } else {
          const trianglesItem = chunk.geometry.getLayoutItem("indices");
          const triangles = trianglesItem.getBuffer();
          frameData.trianglesChunks.push(triangles);
          transferables.push(triangles.buffer);
          trianglesItem.setIsDetached(true);
        }
      }

      ICPData.push(frameData);
    }
  }
}

function ICPDataToObjects(objects, ICPData) {
  let dataPtr = 0;
  for (const objectUUID of objects) {
    const object = workspace.getObjectByUUID(objectUUID);
    const filterFrames = object.type === objectType.SCAN;

    for (const frame of object.frames) {
      if (filterFrames && !frame.isKeyFrame() && !frame.textureList.length) {
        continue;
      }
      const frameData = ICPData[dataPtr];

      for (let i=0; i<frame.model.chunks.length; i++) {
        const chunk = frame.model.chunks[i];

        const positionsItem = chunk.geometry.getLayoutItem("a_position");
        const normalsItem = chunk.geometry.getLayoutItem("a_normal");
        const sizeItem = chunk.geometry.getLayoutItem("a_size");
        const trianglesItem = chunk.geometry.getLayoutItem("indices");

        positionsItem.setBuffer(frameData.pointsChunks[i].buffer);
        if (trianglesItem) {
          trianglesItem.setBuffer(frameData.trianglesChunks[i].buffer);
        }
        if (normalsItem) {
          normalsItem.setBuffer(frameData.normalsChunks[i].buffer);
        }
        if (sizeItem) {
          sizeItem.setBuffer(frameData.sizeChunks[i].buffer);
        }
      }

      dataPtr++;
    }
  }
}
